import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { EventType } from "@azure/msal-browser";
import { broadcastResponseToMainFrame } from "@azure/msal-browser/redirect-bridge";
import type { EventMessage, AccountInfo } from "@azure/msal-browser";
import { MsalProvider } from "@azure/msal-react";
import { msalInstance, initializeMsal } from "@/services/authService";
import { watchAbandonedLogout } from "@/lib/courseLogin";
import { isMsalAuthFrame } from "@/lib/msalAuthFrame";

import "@fontsource/source-sans-pro/400.css";
import "@fontsource/source-sans-pro/600.css";
import "@fontsource/source-sans-pro/700.css";
import "@/index.css";
import App from "@/App";

const boot = async () => {
  // redirectUri is the SPA root (no spare blank.html route), so MSAL's hidden
  // iframe for acquireTokenSilent/ssoSilent loads this same entry point. Since
  // MSAL v5 the main window waits for this frame to post the auth response on
  // a BroadcastChannel; without it every silent renewal times out and the
  // course fails with session_expired. The app itself must not boot here.
  if (isMsalAuthFrame()) {
    await broadcastResponseToMainFrame().catch((error: unknown) => {
      console.warn("Could not hand the silent sign-in to the app", error);
    });
    return;
  }

  // Back from the Microsoft logout screen may restore this document from the
  // back/forward cache. The watcher has to be registered on the frozen page.
  watchAbandonedLogout(msalInstance);

  // Initialize MSAL instance (required for MSAL v3+)
  await initializeMsal();

  // Handle redirect response (e.g. when popup was blocked and loginRedirect was used).
  // An error in the returned hash (declined consent, AADSTS errors) must not
  // leave the app blank: boot without an account and let the route decide.
  const redirectResponse = await msalInstance
    .handleRedirectPromise()
    .catch((error: unknown) => {
      console.warn("Sign-in redirect returned an error", error);
      return null;
    });
  if (redirectResponse?.account) {
    msalInstance.setActiveAccount(redirectResponse.account);
  }

  // Account selection logic
  const accounts = msalInstance.getAllAccounts();
  if (accounts.length > 0 && !msalInstance.getActiveAccount()) {
    msalInstance.setActiveAccount(accounts[0]);
  }

  // Handle successful logins
  msalInstance.addEventCallback((event: EventMessage) => {
    if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
      const payload = event.payload as { account: AccountInfo };
      const account = payload.account;
      msalInstance.setActiveAccount(account);
    }
  });

  createRoot(document.getElementById("root")!).render(
    <StrictMode>
      <MsalProvider instance={msalInstance}>
        <App />
      </MsalProvider>
    </StrictMode>
  );
};

await boot();
